import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { documentsApi, teacherApi } from '../api';
import {
  Badge,
  DataTable,
  EmptyState,
  Field,
  HeroPanel,
  MiniBars,
  Panel,
  PortalShell,
  SelectInput,
  StatCard,
  TextInput,
  Toast,
  getSessionUser
} from '../components/PortalUI';
import { dateOnly, downloadCsv, parseCsv, percent, toArray } from '../utils';

const navItems = ['Overview', 'Attendance', 'Marks', 'Students', 'Audit Logs', 'Documents', 'Reports', 'Notifications', 'Settings'];
const departments = ['CSE', 'IT', 'ECE', 'Mechanical', 'Civil'];
const years = ['1', '2', '3', '4'];
const sections = ['A', 'B', 'C'];
const subjects = ['DBMS', 'Cloud Computing', 'Data Structures', 'Operating Systems', 'Computer Networks'];
const examTypes = ['Quiz', 'Midterm', 'Practical', 'End Semester'];

const initialFilters = {
  department: 'CSE',
  year: '3',
  section: 'A',
  subject: 'DBMS',
  date: new Date().toISOString().slice(0, 10),
  examType: 'Midterm',
  search: ''
};

function exportExcel(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const html = `<table><thead><tr>${headers.map((h) => `<th>${h}</th>`).join('')}</tr></thead><tbody>${rows.map((row) => `<tr>${headers.map((h) => `<td>${row[h] ?? ''}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
  const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function TeacherDashboard() {
  const [active, setActive] = useState('Overview');
  const [filters, setFilters] = useState(initialFilters);
  const [dashboard, setDashboard] = useState(null);
  const [roster, setRoster] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [marks, setMarks] = useState([]);
  const [students, setStudents] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [reports, setReports] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const teacherId = getSessionUser();

  const load = useCallback(async () => {
    setLoading(true);
    const [overview, attendanceData, marksData, studentData, logData, notificationData] = await Promise.all([
      teacherApi.dashboard().catch(() => null),
      teacherApi.attendance(filters).catch(() => ({ roster: [], records: [] })),
      teacherApi.marks(filters).catch(() => ({ roster: [], records: [] })),
      teacherApi.students(filters).catch(() => ({ items: [] })),
      teacherApi.auditLogs().catch(() => ({ items: [] })),
      teacherApi.notifications().catch(() => ({ items: [] }))
    ]);
    setDashboard(overview);
    setRoster(attendanceData.roster || marksData.roster || []);
    setAttendance(attendanceData.records || []);
    setMarks(marksData.records || []);
    setStudents(toArray(studentData));
    setAuditLogs(toArray(logData));
    setNotifications(toArray(notificationData));
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);


  const filteredRoster = useMemo(() => {
    const term = filters.search.toLowerCase();
    return roster.filter((student) => !term || `${student.name} ${student.studentName} ${student.rollNumber} ${student.studentId}`.toLowerCase().includes(term));
  }, [filters.search, roster]);

  const selectedAttendance = useMemo(() => {
    const map = new Map(attendance.map((item) => [item.studentId, item]));
    return filteredRoster.map((student) => map.get(student.studentId) || {
      studentId: student.studentId,
      rollNumber: student.rollNumber,
      studentName: student.name || student.studentName,
      status: 'present',
      remarks: ''
    });
  }, [attendance, filteredRoster]);

  const selectedMarks = useMemo(() => {
    const map = new Map(marks.map((item) => [item.studentId, item]));
    return filteredRoster.map((student) => map.get(student.studentId) || {
      studentId: student.studentId,
      rollNumber: student.rollNumber,
      studentName: student.name || student.studentName,
      marksObtained: '',
      maximumMarks: 100,
      remarks: ''
    });
  }, [filteredRoster, marks]);

  const updateAttendance = (studentId, patch) => {
    setAttendance(selectedAttendance.map((row) => row.studentId === studentId ? { ...row, ...patch } : row));
  };

  const updateMarks = (studentId, patch) => {
    setMarks(selectedMarks.map((row) => row.studentId === studentId ? { ...row, ...patch } : row));
  };

  const saveAttendance = async () => {
    await teacherApi.saveAttendance({ ...filters, records: selectedAttendance });
    setToast('Attendance saved');
    load();
  };

  const saveMarks = async () => {
    const invalid = selectedMarks.find((row) => Number(row.marksObtained || 0) > Number(row.maximumMarks || 0));
    if (invalid) {
      setToast(`Marks exceed maximum for ${invalid.studentName}`);
      return;
    }
    await teacherApi.saveMarks({ ...filters, records: selectedMarks });
    setToast('Marks saved');
    load();
  };

  const importMarks = async (file) => {
    if (!file) return;
    const rows = parseCsv(await file.text());
    setMarks(rows.map((row) => ({ ...row, marksObtained: Number(row.marksObtained || 0), maximumMarks: Number(row.maximumMarks || 100) })));
    setToast(`${rows.length} marks imported`);
  };

  const loadDocuments = async (studentId) => {
    const docs = await documentsApi.list(studentId).catch(() => []);
    setDocuments(docs.map((doc) => ({ ...doc, studentId })));
    setActive('Documents');
  };

  const generateReports = async () => {
    const data = await teacherApi.reports(filters).catch(() => ({ attendance: [], marks: [], students: [] }));
    setReports(data);
    setToast('Reports generated');
  };

  const attendanceColumns = [
    { key: 'rollNumber', label: 'Roll No', render: (row) => <span className="font-black">{row.rollNumber}</span> },
    { key: 'studentName', label: 'Student Name', render: (row) => <span className="font-bold">{row.studentName}</span> },
    { key: 'status', label: 'Status', render: (row) => <button onClick={() => updateAttendance(row.studentId, { status: row.status === 'present' ? 'absent' : 'present' })} className={`rounded-full px-3 py-1 text-xs font-black ${row.status === 'present' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{row.status}</button> },
    { key: 'remarks', label: 'Remarks', render: (row) => <TextInput value={row.remarks || ''} onChange={(e) => updateAttendance(row.studentId, { remarks: e.target.value })} placeholder="Optional" className="focus:ring-amber-100 focus:border-amber-500" /> }
  ];

  const marksColumns = [
    { key: 'rollNumber', label: 'Roll No', render: (row) => <span className="font-black">{row.rollNumber}</span> },
    { key: 'studentName', label: 'Student Name', render: (row) => <span className="font-bold">{row.studentName}</span> },
    { key: 'marksObtained', label: 'Marks', render: (row) => <TextInput type="number" value={row.marksObtained} onChange={(e) => updateMarks(row.studentId, { marksObtained: e.target.value })} className="w-24 focus:ring-amber-100 focus:border-amber-500" /> },
    { key: 'maximumMarks', label: 'Max', render: (row) => <TextInput type="number" value={row.maximumMarks} onChange={(e) => updateMarks(row.studentId, { maximumMarks: e.target.value })} className="w-24 focus:ring-amber-100 focus:border-amber-500" /> },
    { key: 'grade', label: 'Grade', render: (row) => <Badge tone={percent(row.marksObtained, row.maximumMarks) >= 60 ? 'emerald' : 'rose'}>{percent(row.marksObtained, row.maximumMarks) >= 90 ? 'A+' : percent(row.marksObtained, row.maximumMarks) >= 80 ? 'A' : percent(row.marksObtained, row.maximumMarks) >= 70 ? 'B' : percent(row.marksObtained, row.maximumMarks) >= 60 ? 'C' : 'F'}</Badge> },
    { key: 'remarks', label: 'Remarks', render: (row) => <TextInput value={row.remarks || ''} onChange={(e) => updateMarks(row.studentId, { remarks: e.target.value })} className="focus:ring-amber-100 focus:border-amber-500" /> }
  ];

  const studentColumns = [
    { key: 'studentId', label: 'Enrollment', render: (row) => <span className="font-black text-blue-600">{row.studentId}</span> },
    { key: 'rollNumber', label: 'Roll No' },
    { key: 'name', label: 'Name', render: (row) => <span className="font-bold">{row.name}</span> },
    { key: 'department', label: 'Department', render: (row) => <Badge tone="blue">{row.department}</Badge> },
    { key: 'year', label: 'Year' },
    { key: 'section', label: 'Section', render: (row) => row.section || 'A' },
    { key: 'actions', label: 'Actions', render: (row) => <button onClick={() => loadDocuments(row.studentId)} className="rounded-lg bg-violet-50 px-3 py-1.5 text-xs font-black text-violet-700 hover:bg-violet-100">Documents</button> }
  ];

  const auditColumns = [
    { key: 'timestamp', label: 'Timestamp', render: (row) => row.timestamp?.replace('T', ' ').split('.')[0] || '-' },
    { key: 'action', label: 'Action', render: (row) => <Badge tone="amber">{row.action}</Badge> },
    { key: 'student', label: 'Student', render: (row) => row.after?.studentName || row.after?.name || row.targetId },
    { key: 'userId', label: 'Performed By' },
    { key: 'ipAddress', label: 'IP Address', render: (row) => row.ipAddress || 'client' }
  ];

  const filterBar = ['Attendance', 'Marks', 'Students', 'Reports'].includes(active) && (
    <Panel>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-7">
        <Field label="Department"><SelectInput value={filters.department} onChange={(e) => setFilters({ ...filters, department: e.target.value })}>{departments.map((item) => <option key={item}>{item}</option>)}</SelectInput></Field>
        <Field label="Year"><SelectInput value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })}>{years.map((item) => <option key={item} value={item}>Year {item}</option>)}</SelectInput></Field>
        <Field label="Section"><SelectInput value={filters.section} onChange={(e) => setFilters({ ...filters, section: e.target.value })}>{sections.map((item) => <option key={item}>{item}</option>)}</SelectInput></Field>
        <Field label="Subject"><SelectInput value={filters.subject} onChange={(e) => setFilters({ ...filters, subject: e.target.value })}>{subjects.map((item) => <option key={item}>{item}</option>)}</SelectInput></Field>
        <Field label="Exam"><SelectInput value={filters.examType} onChange={(e) => setFilters({ ...filters, examType: e.target.value })}>{examTypes.map((item) => <option key={item}>{item}</option>)}</SelectInput></Field>
        <Field label="Date"><TextInput type="date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })} /></Field>
        <Field label="Search"><TextInput value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="Name or roll" /></Field>
      </div>
    </Panel>
  );

  return (
    <PortalShell
      role="teacher"
      title={active}
      subtitle="Attendance, examination marks, reports and student-only audit visibility in a focused teaching workspace."
      navItems={navItems}
      active={active}
      onNavChange={setActive}
      actions={<button onClick={load} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 transition-all duration-150 hover:bg-slate-50 active:scale-[0.97] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800">Refresh</button>}
    >
      <div>
        {toast && <Toast message={toast} type={toast.toLowerCase().includes('exceed') ? 'error' : 'success'} onClose={() => setToast('')} />}
        <div className="space-y-6">
          <HeroPanel role="teacher" eyebrow="Teaching operations" title={`Good day, ${teacherId}`}>
            Review class attendance, submit marks, generate reports and inspect student-only audit trails without leaving the workspace.
          </HeroPanel>

          {filterBar}

          {active === 'Overview' && (
            <>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5 stagger">
                <StatCard label="Total Students" value={dashboard?.totals?.students || students.length} tone="text-blue-600" />
                <StatCard label="Present Today" value={dashboard?.totals?.presentToday || 0} tone="text-emerald-600" />
                <StatCard label="Absent Today" value={dashboard?.totals?.absentToday || 0} tone="text-rose-600" />
                <StatCard label="Pending Marks" value={dashboard?.totals?.pendingMarkEntries || 0} tone="text-amber-600" />
                <StatCard label="Recent Activity" value={dashboard?.totals?.recentActivities || auditLogs.length} tone="text-violet-600" />
              </div>
              <div className="grid gap-5 lg:grid-cols-2">
                <Panel title="Attendance by Department" subtitle="Present percentage for current day."><MiniBars data={dashboard?.charts?.attendanceByDepartment} suffix="%" color="bg-emerald-500" /></Panel>
                <Panel title="Average Marks by Subject" subtitle="Average marks from saved entries."><MiniBars data={dashboard?.charts?.averageMarksBySubject} color="bg-amber-500" /></Panel>
              </div>
            </>
          )}

          {active === 'Attendance' && (
            <Panel
              title="Take Attendance"
              subtitle={`${selectedAttendance.length} students loaded for ${filters.department} Year ${filters.year} Section ${filters.section}`}
              actions={
                <>
                  <button onClick={() => setAttendance(selectedAttendance.map((row) => ({ ...row, status: 'present' })))} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-black text-white">Mark all present</button>
                  <button onClick={() => setAttendance(selectedAttendance.map((row) => ({ ...row, status: 'absent' })))} className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-black text-white">Mark all absent</button>
                  <button onClick={() => setConfirm({ title: 'Save attendance?', action: saveAttendance })} className="rounded-lg bg-slate-950 px-4 py-2 text-sm font-black text-white">Save</button>
                </>
              }
            >
              <DataTable columns={attendanceColumns} rows={selectedAttendance} loading={loading} empty="No students found for selected class." rowKey={(row) => row.studentId} />
            </Panel>
          )}

          {active === 'Marks' && (
            <Panel
              title="Manage Examination Marks"
              subtitle="Bulk entry with maximum marks validation."
              actions={
                <>
                  <label className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-black text-slate-700">
                    Import CSV
                    <input type="file" accept=".csv" className="hidden" onChange={(e) => importMarks(e.target.files?.[0])} />
                  </label>
                  <button onClick={() => downloadCsv('teacher-marks.csv', selectedMarks)} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-black text-white">Export CSV</button>
                  <button onClick={() => setConfirm({ title: 'Save marks?', action: saveMarks })} className="rounded-lg bg-slate-950 px-4 py-2 text-sm font-black text-white">Save</button>
                </>
              }
            >
              <DataTable columns={marksColumns} rows={selectedMarks} loading={loading} empty="No students found for selected class." rowKey={(row) => row.studentId} />
            </Panel>
          )}

          {active === 'Students' && (
            <Panel title="Student Records" subtitle="Search and review profiles, attendance context, marks context and documents.">
              <DataTable columns={studentColumns} rows={students} loading={loading} empty="No student records found." rowKey={(row) => row.studentId} />
            </Panel>
          )}

          {active === 'Audit Logs' && (
            <Panel title="Student Audit Logs Only" subtitle="Employee and administrative audit data is intentionally hidden.">
              <DataTable columns={auditColumns} rows={auditLogs} loading={loading} empty="No student activity logs found." />
            </Panel>
          )}

          {active === 'Documents' && (
            <Panel title="Read-only Student Documents" subtitle="Open Students and select Documents to load files.">
              {documents.length ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {documents.map((doc) => (
                    <a key={doc.documentId} href={doc.downloadUrl || '#'} target="_blank" rel="noreferrer" className="rounded-lg border border-slate-200 bg-white p-4 hover:bg-slate-50">
                      <p className="font-black text-slate-800">{doc.fileName}</p>
                      <p className="mt-1 text-xs font-bold text-slate-400">{doc.studentId} - {doc.status} - {dateOnly(doc.createdAt)}</p>
                    </a>
                  ))}
                </div>
              ) : <EmptyState title="No documents selected" text="Choose a student record and open Documents." />}
            </Panel>
          )}

          {active === 'Reports' && (
            <Panel title="Reports" subtitle="Generate attendance, marks and performance reports.">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <button onClick={generateReports} className="rounded-lg bg-amber-500 p-4 font-black text-white">Generate</button>
                <button onClick={() => downloadCsv('attendance-report.csv', reports?.attendance || [])} className="rounded-lg bg-blue-600 p-4 font-black text-white">Attendance CSV</button>
                <button onClick={() => downloadCsv('marks-report.csv', reports?.marks || [])} className="rounded-lg bg-emerald-600 p-4 font-black text-white">Marks CSV</button>
                <button onClick={() => exportExcel('performance-report.xls', reports?.students || [])} className="rounded-lg bg-violet-600 p-4 font-black text-white">Excel</button>
              </div>
            </Panel>
          )}

          {active === 'Notifications' && (
            <Panel title="Notifications" subtitle="Pending attendance, marks and student activity reminders.">
              {notifications.length ? <div className="grid gap-3">{notifications.map((item) => <div key={item.notificationId} className="rounded-lg border border-slate-200 bg-white p-4"><div className="flex items-start justify-between gap-3"><div><p className="font-black text-slate-900">{item.title}</p><p className="mt-1 text-sm text-slate-500">{item.message}</p></div><Badge tone={item.priority === 'high' ? 'rose' : 'amber'}>{item.priority}</Badge></div></div>)}</div> : <EmptyState title="No notifications" text="Reminders will appear when records require action." />}</Panel>
          )}

          {active === 'Settings' && (
            <Panel title="Settings" subtitle="Teacher preferences and local display settings.">
              <div className="grid max-w-3xl gap-4 sm:grid-cols-2">
                <Field label="Display name"><TextInput placeholder="Teacher name" /></Field>
                <Field label="Email"><TextInput placeholder="teacher@college.edu" /></Field>
                <label className="flex items-center gap-3 rounded-lg bg-slate-50 p-4 text-sm font-black text-slate-700"><input type="checkbox" defaultChecked /> Email notifications</label>
                <label className="flex items-center gap-3 rounded-lg bg-slate-50 p-4 text-sm font-black text-slate-700"><input type="checkbox" defaultChecked /> Pending marks reminders</label>
              </div>
            </Panel>
          )}
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/60 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-2xl animate-float-in dark:bg-slate-900">
            <h3 className="text-xl font-black text-slate-950">{confirm.title}</h3>
            <p className="mt-2 text-sm text-slate-500">This will update records and write a student audit log.</p>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setConfirm(null)} className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-black text-slate-700">Cancel</button>
              <button onClick={async () => { await confirm.action(); setConfirm(null); }} className="rounded-lg bg-slate-950 px-4 py-2 text-sm font-black text-white">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </PortalShell>
  );
}

export default TeacherDashboard;
